import React, { useState, useEffect } from "react";
import { Drawer, Table, TableColumnsType, Empty, Spin, Flex } from "antd";
import purchaseOrderCreation from "../../api/purchase-orders/pruchaseOrderCreation";
import { formatCurrency } from "../../utils/Utils";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

interface PurchaseOrdersDrawerProps {
  visible: boolean;
  onClose: () => void;
  site: string;
  contractor: string;
  dateRange: { from: string; to: string };
  categoryId?: string;
}

const PurchaseOrdersDrawer: React.FC<PurchaseOrdersDrawerProps> = ({
  visible,
  onClose,
  site,
  contractor,
  dateRange,
  categoryId,
}) => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();

  // Calculate delivery/overdue days
  const calculateDeliveryDays = (deliveryDate: string | null | undefined) => {
    if (!deliveryDate) {
      return { text: "N/A", isOverdue: false, days: 0 };
    }
    const today = dayjs();
    const delivery = dayjs(deliveryDate);
    const diffDays = delivery.diff(today, "day");

    if (diffDays < 0) {
      return { text: `Overdue by ${Math.abs(diffDays)} Days`, isOverdue: true, days: Math.abs(diffDays) };
    } else if (diffDays === 0) {
      return { text: "Due Today", isOverdue: false, days: 0 };
    } else {
      return { text: `Delivery in ${diffDays} Days`, isOverdue: false, days: diffDays };
    }
  };

  const columns: TableColumnsType<any> = [
     {
      title: "PO #",
      dataIndex: "poId",
      key: "poId",
      width: 150,
      render: (text: any, record: any) => (
        <span
          className="inlineTableCellLink"
          onClick={() => {
            if (record?.status == "Draft") {
              navigate(`/purchase-order-new/`, {
                state: {
                  draftId: record?.PoNumber,
                  fromPurchaseOrdersDrawer: true,
                  previousPath: "/dashboard",
                  previousState: {
                    openPurchaseOrdersDrawer: true,
                    site,
                    contractor,
                    dateRange,
                    categoryId,
                  },
                },
              });
            } else {
              navigate(`/view-purchase-order/${record.PoNumber}`, {
                state: {
                  fromPurchaseOrdersDrawer: true,
                  previousPath: "/dashboard",
                  previousState: {
                    openPurchaseOrdersDrawer: true,
                    site,
                    contractor,
                    dateRange,
                    categoryId,
                  },
                },
              });
            }
          }}
        >
          {text}
        </span>
      ),
    },
    {
      title: "PO Date",
      dataIndex: "poDate",
      key: "poDate",
      width: 150,
      render: (date: any) =>
        date ? dayjs(date).format("DD-MMM-YYYY") : "-",
    },
    {
      title: "Supplier",
      dataIndex: "Supplier",
      key: "Supplier",
      width: 200,
      fixed: "left",
    },
    {
      title: "Amount",
      dataIndex: "Amount",
      key: "Amount",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, record.currencyCode || "INR")}</span>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "actualStatus",
      key: "actualStatus",
      fixed: "right",
      width: 150,
      align: "left",
      render: (text, record) => {
        const status = text || record.status;
        return (
          <>
            <div className="justify-start align-center">
              <h6
                className={`tb-status ms-1`}
                style={{
                  fontSize: "14px",
                  color:
                    status === "Approved"
                      ? "#28A745" // Green
                      : status === "Open"
                      ? "#007BFF" // Blue
                      : status === "In Progress"
                      ? "#FD7E14" // Orange
                      : status === "Rejected"
                      ? "#DC3545" // Red
                      : status === "Pending"
                      ? "#FFC107" // Yellow
                      : "black", // Default color
                }}
              >
                {status}
              </h6>
            </div>
          </>
        );
      },
    },
    {
      title: "Delivery Days",
      key: "DeliveryDays",
      width: 200,
      render: (text: any, record: any) => {
        // Use DeliveryDays from API response if available, otherwise calculate from order_needby
        if (record.DeliveryDays) {
          const isOverdue = record.DeliveryDays.includes("Overdue");
          return (
            <span
              style={{
                color: isOverdue ? "#ff4d4f" : "#52c41a",
                fontWeight: isOverdue ? "bold" : "normal",
              }}
            >
              {record.DeliveryDays}
            </span>
          );
        }
        // Fallback to calculation if DeliveryDays is not provided
        const deliveryDate = record.order_needby;
        const deliveryInfo = calculateDeliveryDays(deliveryDate);
        return (
          <span
            style={{
              color: deliveryInfo.isOverdue ? "#ff4d4f" : deliveryInfo.text === "N/A" ? "#666" : "#52c41a",
              fontWeight: deliveryInfo.isOverdue ? "bold" : "normal",
            }}
          >
            {deliveryInfo.text}
          </span>
        );
      },
    },
  ];

  const fetchPurchaseOrders = async (page: number = 1, pageSize: number = 10) => {
    setIsLoading(true);
    try {
      const requestBody: any = {
        startDate: dateRange.from,
        endDate: dateRange.to,
      };

      if (site) {
        requestBody.siteId = site;
      }

      if (currentUser?.orgid) {
        requestBody.orgId = currentUser.orgid;
      }

      if (categoryId) {
        requestBody.categoryId = categoryId;
      }

      if (contractor) {
        requestBody.contractorId = contractor;
      }

      const res = await purchaseOrderCreation.fetchPurchaseOrderDetails(
        requestBody
      );
      if (res?.status) {
        setData(res.response?.data || []);
        // Handle pagination if available, otherwise use client-side pagination
        if (res.response?.currentPage !== undefined) {
          setPagination({
            current: res.response?.currentPage || page,
            pageSize: res.response?.pageSize || pageSize,
            total: res.response?.totalRecords || res.response?.data?.length || 0,
          });
        } else {
          // If no pagination info, assume all data is returned
          setPagination({
            current: page,
            pageSize: pageSize,
            total: res.response?.data?.length || 0,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (visible && dateRange.from && dateRange.to) {
      fetchPurchaseOrders(1, 10);
    }
  }, [visible, site, contractor, dateRange, categoryId]);

  const handleTableChange = (newPagination: any) => {
    fetchPurchaseOrders(newPagination.current, newPagination.pageSize);
  };

  return (
    <Drawer
      title="Purchase Orders"
      placement="right"
      onClose={onClose}
      open={visible}
      width="85%"
    >
      {isLoading ? (
        <Flex align="center" gap="middle" className="pageLoader">
          <Spin size="large" className="m-auto" />
        </Flex>
      ) : data.length === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Empty description="No Purchase Orders Found" />
        </div>
      ) : (
        <Table
          className="customTable"
          columns={columns}
          dataSource={data}
          rowKey={(record) => record.PoNumber || record.poId || record.orgId}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30", "50"],
          }}
          onChange={handleTableChange}
          bordered
          size="middle"
          scroll={{ x: 1200 }}
        />
      )}
    </Drawer>
  );
};

export default PurchaseOrdersDrawer;

